"use client"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { TrendingUp, Shield, FileCheck, Users, Target, ArrowRight } from "lucide-react"
import Link from "next/link"
import Image from "next/image"
import { useBlog } from "@/contexts/blog-context"
import { useEffect, useState } from "react"

export function BlogCategories() {
  const { categories, getCategoryCount } = useBlog()
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(true)

  // Manual blog posts for Recent Articles section
  const manualBlogPosts = [
    {
      id: "1",
      title: "Leading with Purpose: The Role of Leadership in HMOs",
      slug: "leading-with-purpose-the-role-of-leadership-in-hmos",
      category: "Leadership & Strategy",
      readTime: "5 min read",
      image: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcT775qsaWZDwQqzEWgEALSAjSlNR-lJoovAVA&s",
      excerpt: "In today's evolving healthcare landscape, leadership in Health Maintenance Organizations (HMOs)"
    },
    {
      id: "2", 
      title: "Scaling HMOs with ERP: The Future of Healthcare Management",
      slug: "scaling-hmos-with-erp-the-future-of-healthcare-management",
      category: "Scaling & Growth",
      readTime: "7 min read",
      image: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSzurY4YVPrtGmAMEP9c2aXvbnmnocsxRrjAA&s",
      excerpt: "In an era where efficiency, transparency, and member experience define success, Health Maintenance Organizations (HMOs)"
    },
    {
      id: "3",
      title: "7 Practical Tips to Maximize Your Use of Octohealth ERP in Healthcare Management",
      slug: "7-practical-tips-to-maximize-your-use-of-octohealth-erp-in-healthcare-management", 
      category: "ERP Tips",
      readTime: "6 min read",
      image: "https://img.freepik.com/free-photo/person-working-html-computer_23-2150038844.jpg?semt=ais_hybrid&w=740",
      excerpt: "As the healthcare industry rapidly evolves, technology continues to reshape how we..."
    },
    {
      id: "4",
      title: "The Future of Claims Processing: How AI is Transforming African HMOs",
      slug: "the-future-of-claims-processing-how-ai-is-transforming-african-hmos",
      category: "AI", 
      readTime: "8 min read",
      image: "https://editorial.actuview.com/wp-content/uploads/2025/04/AI_AfricaRe_ATI.png",
      excerpt: "AI is revolutionizing claims processing for African Health Maintenance Organizations (HMOs)"
    }
  ]

  const [publishedPosts] = useState(manualBlogPosts)

  console.log("BlogCategories component rendered")
  console.log("Categories from context:", categories)
  console.log("Manual published posts:", publishedPosts)
  
  useEffect(() => {

    // Load category counts
    const loadCounts = async () => {
      const counts: Record<string, number> = {}
      // Deduplicate by name (case-insensitive)
      const uniqueNames = Array.from(new Set(categories.map((c) => c.name.trim().toLowerCase())))
      for (const name of uniqueNames) {
        const displayName = categories.find((c) => c.name.trim().toLowerCase() === name)?.name || name
        counts[displayName] = await getCategoryCount(displayName)
      }
      setCategoryCounts(counts)
      setLoading(false)
    }

    loadCounts()
  }, [categories, getCategoryCount])

  // Icon and color mappings
  const categoryIcons = {
    "Operations & Efficiency": TrendingUp,
    "Claims & Fraud": Shield,
    "Regulatory Compliance": FileCheck,
    "Scaling & Growth": Users,
    "Leadership & Strategy": Target,
  }

  const categoryColors = {
    "Operations & Efficiency": "bg-blue-100 text-blue-600",
    "Claims & Fraud": "bg-red-100 text-red-600",
    "Regulatory Compliance": "bg-green-100 text-green-600",
    "Scaling & Growth": "bg-purple-100 text-purple-600",
    "Leadership & Strategy": "bg-orange-100 text-orange-600",
  }

  // Do not block the entire section while loading; sidebar should render immediately

  // Build unique categories preserving first occurrence order
  const uniqueCategories = Array.from(
    new Map(categories.map((c) => [c.name.trim().toLowerCase(), c])).values(),
  )

  // Always render the sidebar; show categories only if available
  const hasCategories = uniqueCategories.length > 0

  return (
    <section className="py-20 bg-gradient-to-br from-[#1a85ca]/5 via-white to-[#EA3056]/10">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`grid gap-12 ${hasCategories ? "lg:grid-cols-3" : "lg:grid-cols-1"}`}>
          {/* Categories */}
          {hasCategories && (
          <div className="lg:col-span-2">
            <h2 className="text-3xl lg:text-5xl font-light text-black mb-8">Browse by Category</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {uniqueCategories.map((category) => {
                const IconComponent = categoryIcons[category.name as keyof typeof categoryIcons] || TrendingUp
                const colorClass =
                  categoryColors[category.name as keyof typeof categoryColors] || "bg-gray-100 text-gray-600"
                const count = categoryCounts[category.name] || 0

                return (
                  <Card
                    key={category.id}
                    className="bg-white/70 backdrop-blur-md border border-white/30 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer group transform hover:scale-[1.02]"
                  >
                    <CardHeader>
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-lg ${colorClass}`}>
                          <IconComponent className="h-6 w-6" />
                        </div>
                        <div>
                          <CardTitle className="text-lg group-hover:text-[#1a85ca] transition-colors">
                            {category.name}
                          </CardTitle>
                          <Badge variant="outline" className="text-xs mt-1">
                            {count} {count === 1 ? "article" : "articles"}
                          </Badge>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 mb-4">
                        {category.name === "Operations & Efficiency" &&
                          "Streamline your HMO operations with proven strategies and best practices"}
                        {category.name === "Claims & Fraud" &&
                          "Advanced techniques for claims management and fraud prevention"}
                        {category.name === "Regulatory Compliance" &&
                          "Stay compliant with evolving healthcare regulations across Africa"}
                        {category.name === "Scaling & Growth" &&
                          "Strategies for sustainable growth and market expansion"}
                        {category.name === "Leadership & Strategy" &&
                          "Executive insights and strategic planning for HMO leaders"}
                      </p>
                      <Link
                        href={`/blog/category/${encodeURIComponent(category.name)}`}
                        className="inline-flex items-center text-[#1a85ca] hover:text-[#0630a0] font-medium transition-colors"
                      >
                        Explore Category
                        <ArrowRight className="h-4 w-4 ml-1" />
                      </Link>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          </div>
          )}

          {/* Recent Articles Sidebar with Rectangular Layout */}
          <div>
            <h3 className="text-2xl font-light text-gray-900 mb-8">Recent Articles</h3>
            <div className="space-y-4">
              {publishedPosts.length > 0 ? (
                publishedPosts.map((article) => (
                  <Card
                    key={(article as any).id}
                    className="bg-white/70 backdrop-blur-md border border-white/30 shadow-sm hover:shadow-md transition-shadow cursor-pointer overflow-hidden"
                  >
                    <Link href={`/blog/${(article as any).slug}`} className="block">
                      <div className="flex gap-3 p-3">
                        {/* Squared thumbnail */}
                        <div className="relative w-16 h-16 flex-shrink-0 overflow-hidden rounded-lg">
                          <Image
                            src={(article as any).image || "/placeholder.svg?height=64&width=64&text=Blog"}
                            alt={(article as any).title}
                            width={64}
                            height={64}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        {/* Content next to thumbnail */}
                        <div className="flex-1 min-w-0">
                          {/* Inline post title */}
                          <h4 className="font-semibold text-gray-900 mb-1 hover:text-[#1a85ca] transition-colors text-sm leading-tight line-clamp-2">
                            {(article as any).title}
                          </h4>
                          {/* Excerpt below title */}
                          <p className="text-xs text-gray-600 mb-2 line-clamp-2 leading-relaxed">
                            {(article as any).excerpt}
                          </p>
                          {/* Category and read time */}
                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <Badge variant="outline" className="text-xs">
                              {(article as any).category}
                            </Badge>
                            <span>{(article as any).readTime}</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </Card>
                ))
              ) : (
                <p className="text-gray-600">No recent articles available.</p>
              )}
            </div>

            {/* View All Articles Link */}
            {publishedPosts.length > 0 && (
              <div className="mt-6">
                <Link
                  href="/blog"
                  className="inline-flex items-center text-[#2d86c7] hover:text-[#2d86c7] font-medium transition-colors"
                >
                  View All Articles
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
